/**
 * Date coercion for values read out of Firestore.
 *
 * A timestamp field can reach the UI in several shapes depending on how it was
 * fetched: a Firestore Timestamp instance (client SDK reads), a plain
 * {seconds,nanoseconds} or {_seconds,_nanoseconds} object (anything that has
 * been JSON-serialised, e.g. through a callable function), an ISO string, epoch
 * millis, or a Date already.
 *
 * Passing the wrong shape to `new Date()` yields an Invalid Date, and date-fns
 * throws `RangeError: Invalid time value` on those -- which crashes the whole
 * page. These helpers normalise every shape and return null rather than an
 * unusable Date, so callers can render a placeholder instead of blowing up.
 */

import { formatDistanceToNow } from 'date-fns';

type FirestoreLike =
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | { _seconds: number; _nanoseconds?: number };

/** Converts any Firestore-ish timestamp to a Date, or null if unusable. */
export function toDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'object') {
    const v = value as FirestoreLike;

    // Firestore Timestamp instance
    if ('toDate' in v && typeof v.toDate === 'function') {
      try {
        const d = v.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }

    // Serialised Timestamp — the client SDK uses `seconds`, the admin SDK's
    // JSON form uses `_seconds`.
    const seconds =
      'seconds' in v && typeof v.seconds === 'number'
        ? v.seconds
        : '_seconds' in v && typeof v._seconds === 'number'
          ? v._seconds
          : null;

    if (seconds !== null) {
      const d = new Date(seconds * 1000);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

/**
 * Renders a timestamp as "3 hours ago", or `fallback` when the value is
 * missing or unusable. Safe to call with raw Firestore data.
 */
export function formatRelativeTime(value: unknown, fallback = '—'): string {
  const date = toDate(value);
  if (!date) return fallback;
  return formatDistanceToNow(date, { addSuffix: true });
}
